import React from 'react';
import {
  Checkbox,
  DatePicker,
  Input,
  InputNumber,
  Select,
} from 'antd';
import { useTranslation } from 'react-i18next';
import {
  CustomField,
  FormGroup,
  Grid,
  Switch,
} from '@mss/mss-component-library';
import moment from 'moment';

type Props = {
  customFields: CustomField[];
  enabledFields: Set<string>;
  values: Record<string, any>;
  onToggle: (slug: string) => void;
  onChange: (slug: string, value: any) => void;
  custodians: any;
};

const BulkCustomFields: React.FC<Props> = ({
  customFields,
  enabledFields,
  values,
  onToggle,
  onChange,
  custodians,
}) => {
  const { t } = useTranslation(['asset', 'common']);

  return (
    <>
      {customFields.map(cf => {
        const isEnabled = enabledFields.has(cf.slug);

        return (
          <Grid.Col width={4} key={cf.id}>
            <FormGroup
              label={t(cf.name)}
              input={
                <div className="flex items-start gap-2">
                  <Checkbox
                    checked={isEnabled}
                    onChange={() => onToggle(cf.slug)}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <CustomFieldInput
                      customField={cf}
                      disabled={!isEnabled}
                      value={values[cf.slug]}
                      onChange={val => onChange(cf.slug, val)}
                      custodians={custodians}
                      t={t}
                    />
                  </div>
                </div>
              }
            />
          </Grid.Col>
        );
      })}
    </>
  );
};

type CustomFieldInputProps = {
  customField: CustomField;
  disabled: boolean;
  value: any;
  onChange: (value: any) => void;
  custodians: any;
  t: (key: string) => string;
};

const CustomFieldInput: React.FC<CustomFieldInputProps> = ({
  customField,
  disabled,
  value,
  onChange,
  custodians,
  t,
}) => {
  if (customField.choices?.length > 0) {
    return (
      <Select
        style={{ width: '100%' }}
        disabled={disabled}
        value={value ?? undefined}
        onChange={onChange}
        placeholder={customField.placeholder || `${t('Please select one')}...`}
        showSearch
        allowClear
        optionFilterProp="children"
      >
        {customField.choices.map((choice: any, idx: number) => (
          <Select.Option key={idx} value={choice}>
            {choice}
          </Select.Option>
        ))}
      </Select>
    );
  }

  switch (customField.field_type) {
    case 'integer':
      return (
        <InputNumber
          style={{ width: '100%' }}
          disabled={disabled}
          value={value ?? undefined}
          onChange={onChange}
          placeholder={customField.placeholder || t(customField.name)}
        />
      );

    case 'boolean':
      return (
        <Switch
          disabled={disabled}
          checked={!!value}
          onChange={onChange}
        />
      );

    case 'date_time':
      return (
        <DatePicker
          style={{ width: '100%' }}
          disabled={disabled}
          value={value ? moment(value) : undefined}
          onChange={(_moment, dateString) => onChange(dateString || null)}
          placeholder={customField.placeholder || t('Select a date / time')}
          showTime
        />
      );

    case 'user_group': {
      const flatOptions =
        custodians?.flatMap((group: any) =>
          (group.options ?? []).map((opt: any) => ({
            label: opt.label,
            value: opt.value,
          }))
        ) ?? [];
      return (
        <Select
          style={{ width: '100%' }}
          disabled={disabled}
          value={value ?? undefined}
          onChange={onChange}
          placeholder={
            customField.placeholder || `${t('Please select one')}...`
          }
          showSearch
          allowClear
          optionFilterProp="label"
          options={flatOptions}
        />
      );
    }

    case 'string':
    case 'ip_address':
    default:
      return (
        <Input
          disabled={disabled}
          value={value ?? ''}
          onChange={e => onChange(e.target.value)}
          placeholder={customField.placeholder || t(customField.name)}
          maxLength={250}
        />
      );
  }
};

export default BulkCustomFields;
